/**
 * Done. / ChiroCPD — ActivityForm Feature Component
 *
 * 2-col desktop / 1-col mobile form for logging CPD activities.
 * beforeunload guard via useUnsavedChanges.
 * ErrorSummary on failed submit, focus moved to first error.
 * Hours cap guard — warns when remaining hours would be exceeded.
 */

import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorSummary } from '@/components/ui/ErrorSummary';
import { CapIndicator } from '@/components/ui/CapIndicator';
import { useCapStatus } from '@/hooks/useCapStatus';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { todayIso } from '@/lib/utils';
import type { CreateActivityInput, ActivityType, FormError } from '@/lib/types';

interface ActivityFormProps {
  activityTypes: ActivityType[];
  onSave: (input: CreateActivityInput) => Promise<void>;
  onCancel: () => void;
}

interface FormValues {
  activityTypeId: string;
  title: string;
  description: string;
  hours: string;
  activityDate: string;
}

const EMPTY_FORM: FormValues = {
  activityTypeId: '',
  title: '',
  description: '',
  hours: '',
  activityDate: todayIso(),
};

function validate(values: FormValues, activityTypes: ActivityType[]): FormError[] {
  const errors: FormError[] = [];
  if (!values.activityTypeId) {
    errors.push({ field: 'activityTypeId', message: 'Activity type is required' });
  }
  if (!values.title.trim()) {
    errors.push({ field: 'title', message: 'Title is required' });
  }
  const hours = parseFloat(values.hours);
  if (isNaN(hours) || hours <= 0) {
    errors.push({ field: 'hours', message: 'Hours must be a positive number' });
  }
  if (hours > 20) {
    errors.push({ field: 'hours', message: 'A single activity cannot exceed 20 hours' });
  }
  if (!values.activityDate) {
    errors.push({ field: 'activityDate', message: 'Date is required' });
  }
  // Suppress unused variable warning
  void activityTypes;
  return errors;
}

export function ActivityForm({ activityTypes, onSave, onCancel }: ActivityFormProps): React.ReactElement {
  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<FormError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { cap } = useCapStatus();

  const isDirty =
    values.activityTypeId !== EMPTY_FORM.activityTypeId ||
    values.title !== EMPTY_FORM.title ||
    values.hours !== EMPTY_FORM.hours;

  useUnsavedChanges(isDirty);

  function set(field: keyof FormValues, value: string): void {
    setValues(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    setErrors(prev => prev.filter(e => e.field !== field));
  }

  const handleSubmit = useCallback(async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const validationErrors = validate(values, activityTypes);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setErrors([]);
    try {
      await onSave({
        activityTypeId: values.activityTypeId,
        title: values.title.trim(),
        description: values.description.trim() || null,
        hours: parseFloat(values.hours),
        activityDate: values.activityDate,
      });
      setValues(EMPTY_FORM);
    } catch (err) {
      setErrors([{
        field: 'submit',
        message: err instanceof Error ? err.message : 'Failed to save activity. Please try again.',
      }]);
    } finally {
      setSubmitting(false);
    }
  }, [values, activityTypes, onSave]);

  return (
    <form
      onSubmit={e => { void handleSubmit(e); }}
      noValidate
      className="rounded-[var(--radius-lg)] border border-[var(--border-base)] bg-[var(--surface-base)] p-6 shadow-sm"
      aria-label="Log CPD activity"
    >
      <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4">Log Activity</h2>

      {/* Error summary */}
      {errors.length > 0 && (
        <div className="mb-4">
          <ErrorSummary errors={errors} />
        </div>
      )}

      {/* Cap indicator */}
      {cap && (
        <div className="mb-4">
          <CapIndicator
            current={cap.annualHoursLogged}
            max={cap.annualHourCap}
            unit="hours"
            variant="inline"
          />
        </div>
      )}

      {/* 2-col grid on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Activity type */}
        <div className="flex flex-col gap-1">
          <label
            htmlFor="activityTypeId"
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            Activity type
            <span className="text-[var(--color-error-500)] ml-1" aria-hidden="true">*</span>
          </label>
          <select
            id="activityTypeId"
            value={values.activityTypeId}
            onChange={e => { set('activityTypeId', e.target.value); }}
            required
            aria-required="true"
            aria-invalid={errors.some(e => e.field === 'activityTypeId') ? 'true' : undefined}
            className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm min-h-[44px] bg-[var(--surface-base)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)]"
          >
            <option value="">Select type…</option>
            {activityTypes.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          {errors.find(e => e.field === 'activityTypeId') && (
            <p className="text-xs text-[var(--color-error-500)]" role="alert">
              {errors.find(e => e.field === 'activityTypeId')?.message}
            </p>
          )}
        </div>

        {/* Date */}
        <Input
          id="activityDate"
          label="Activity date"
          type="date"
          value={values.activityDate}
          onChange={v => { set('activityDate', v); }}
          required
          max={todayIso()}
          error={errors.find(e => e.field === 'activityDate')?.message}
        />

        {/* Title — full width */}
        <div className="sm:col-span-2">
          <Input
            id="title"
            label="Title"
            value={values.title}
            onChange={v => { set('title', v); }}
            required
            placeholder="e.g. NZ Chiropractic Conference 2026"
            error={errors.find(e => e.field === 'title')?.message}
          />
        </div>

        {/* Hours */}
        <Input
          id="hours"
          label="Duration (hours)"
          type="number"
          value={values.hours}
          onChange={v => { set('hours', v); }}
          required
          min={0.5}
          max={20}
          step={0.5}
          placeholder="e.g. 4.0"
          error={errors.find(e => e.field === 'hours')?.message}
        />

        {/* Description — full width */}
        <div className="sm:col-span-2 flex flex-col gap-1">
          <label
            htmlFor="description"
            className="text-sm font-medium text-[var(--text-primary)]"
          >
            Description
            <span className="ml-1 text-xs font-normal text-[var(--text-muted)]">(optional)</span>
          </label>
          <textarea
            id="description"
            value={values.description}
            onChange={e => { set('description', e.target.value); }}
            rows={3}
            placeholder="Brief description of the activity content…"
            className="w-full rounded-[var(--radius-md)] border border-[var(--border-base)] px-3 py-2 text-sm bg-[var(--surface-base)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-done-500)] resize-y"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          ariaDisabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save Activity'}
        </Button>
      </div>
    </form>
  );
}
